import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Tab,
	Tabs,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AccountingOrgBar from "./AccountingOrgBar";
import { canLoadAccountingData } from "../useAccountingScope";
import { currencyFormatter } from "utilities/currencyFormatter";

const ReportsTab = ({
	requiresOrganizationSelection,
	sessionOrganization,
	organizationId,
	setOrganizationId,
	fetchAccounts,
	fetchTrialBalance,
	fetchGeneralLedger,
}) => {
	const [reportTab, setReportTab] = useState("trial");
	const [accounts, setAccounts] = useState([]);
	const [dateFrom, setDateFrom] = useState(() => {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
	});
	const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
	const [accountId, setAccountId] = useState("");
	const [trialData, setTrialData] = useState(null);
	const [ledgerData, setLedgerData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const canLoad = canLoadAccountingData(requiresOrganizationSelection, organizationId);

	useEffect(() => {
		const loadAccounts = async () => {
			if (!canLoad) {
				setAccounts([]);
				return;
			}
			const result = await fetchAccounts(organizationId);
			if (result.success) setAccounts(result.accounts);
		};
		loadAccounts();
	}, [organizationId, requiresOrganizationSelection]);

	const formatMoney = (val) => currencyFormatter(parseFloat(val) || 0);

	const runTrialBalance = async () => {
		if (!canLoad) return;
		setLoading(true);
		setError("");
		const params = { date_from: dateFrom, date_to: dateTo };
		if (requiresOrganizationSelection) params.organization_id = organizationId;
		const result = await fetchTrialBalance(params);
		if (result.success) setTrialData(result.data);
		else setError(result.message);
		setLoading(false);
	};

	const runGeneralLedger = async () => {
		if (!canLoad || !accountId) {
			setError("Select an account for the general ledger.");
			return;
		}
		setLoading(true);
		setError("");
		const params = {
			account_id: accountId,
			date_from: dateFrom,
			date_to: dateTo,
		};
		if (requiresOrganizationSelection) params.organization_id = organizationId;
		const result = await fetchGeneralLedger(params);
		if (result.success) setLedgerData(result.data);
		else setError(result.message);
		setLoading(false);
	};

	return (
		<Box>
			<Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
				<AccountingOrgBar
					requiresOrganizationSelection={requiresOrganizationSelection}
					organizationId={organizationId}
					onOrganizationChange={setOrganizationId}
					sessionOrganization={sessionOrganization}
				/>
				<TextField size="small" type="date" label="From" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
				<TextField size="small" type="date" label="To" value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
			</Box>

			<Tabs value={reportTab} onChange={(_, v) => setReportTab(v)} sx={{ mb: 2 }}>
				<Tab value="trial" label="Trial balance" />
				<Tab value="ledger" label="General ledger" />
			</Tabs>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

			{reportTab === "trial" && (
				<Box>
					<Button variant="contained" onClick={runTrialBalance} disabled={!canLoad || loading} sx={{ mb: 2 }}>
						{loading ? "Loading…" : "Run trial balance"}
					</Button>
					{trialData && (
						<>
							{trialData.totals && (
								<Alert severity={trialData.totals.balanced ? "success" : "warning"} sx={{ mb: 2 }}>
									Total debit {formatMoney(trialData.totals.debit)} — credit {formatMoney(trialData.totals.credit)}
									{trialData.totals.balanced ? " (balanced)" : " (not balanced)"}
								</Alert>
							)}
							<TableContainer component={Paper}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Code</TableCell>
											<TableCell>Account</TableCell>
											<TableCell>Type</TableCell>
											<TableCell align="right">Debit</TableCell>
											<TableCell align="right">Credit</TableCell>
											<TableCell align="right">Balance</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{(trialData.rows || []).map((row) => (
											<TableRow key={row.account_id}>
												<TableCell sx={{ fontFamily: "monospace" }}>{row.account_code}</TableCell>
												<TableCell>{row.account_name}</TableCell>
												<TableCell>{row.account_type}</TableCell>
												<TableCell align="right">{formatMoney(row.debit)}</TableCell>
												<TableCell align="right">{formatMoney(row.credit)}</TableCell>
												<TableCell align="right">{formatMoney(row.balance)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</>
					)}
				</Box>
			)}

			{reportTab === "ledger" && (
				<Box>
					<Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
						<FormControl size="small" sx={{ minWidth: 240 }}>
							<InputLabel>Account</InputLabel>
							<Select label="Account" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
								{accounts.map((a) => (
									<MenuItem key={a.id} value={a.id}>{a.code} — {a.name}</MenuItem>
								))}
							</Select>
						</FormControl>
						<Button variant="contained" onClick={runGeneralLedger} disabled={!canLoad || !accountId || loading}>
							{loading ? "Loading…" : "Run general ledger"}
						</Button>
					</Box>
					{ledgerData && (
						<>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
								{ledgerData.account?.code} — {ledgerData.account?.name}
							</Typography>
							<TableContainer component={Paper}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Date</TableCell>
											<TableCell>Entry #</TableCell>
											<TableCell>Description</TableCell>
											<TableCell align="right">Debit</TableCell>
											<TableCell align="right">Credit</TableCell>
											<TableCell align="right">Running balance</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{(ledgerData.rows || []).map((row, idx) => (
											<TableRow key={`${row.entry_id}-${idx}`}>
												<TableCell>{row.entry_date}</TableCell>
												<TableCell sx={{ fontFamily: "monospace" }}>{row.entry_number}</TableCell>
												<TableCell>{row.description}</TableCell>
												<TableCell align="right">{formatMoney(row.debit)}</TableCell>
												<TableCell align="right">{formatMoney(row.credit)}</TableCell>
												<TableCell align="right">{formatMoney(row.running_balance)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</>
					)}
				</Box>
			)}

			{loading && (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			)}
		</Box>
	);
};

export default ReportsTab;
